<template>
  <b-form>
    <b-row>
      <b-col
        v-for="field in fields"
        :key="field.key"
        :cols="field.cols || 6"
        class="mb-1"
      >
        <b-form-group
          :label="$tc(field.label, 1)"
          :label-for="`bulk-${field.key}`"
          class="text-capitalize"
        >
          <!-- Type: select -->
          <v-select
            v-if="field.type === 'select'"
            v-model="localFormData[field.key]"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="getFieldOptions(field)"
            :clearable="field.clearable !== false"
            :reduce="field.reduce"
            :label="field.optionLabel || 'label'"
            :input-id="`bulk-${field.key}`"
            :placeholder="fieldPlaceholder(field)"
            :disabled="isFieldDisabled(field)"
            append-to-body
            :calculate-position="withPopper"
          />

          <!-- Type: multiselect (an emptied selection returns to null = untouched) -->
          <v-select
            v-else-if="field.type === 'multiselect'"
            :value="localFormData[field.key]"
            @input="setMultiselectValue(field, $event)"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="getFieldOptions(field)"
            :reduce="field.reduce"
            :label="field.optionLabel || 'label'"
            :input-id="`bulk-${field.key}`"
            :placeholder="fieldPlaceholder(field)"
            :disabled="isFieldDisabled(field)"
            multiple
            :close-on-select="false"
            append-to-body
            :calculate-position="withPopper"
          />

          <!-- Type: text -->
          <b-form-input
            v-else-if="field.type === 'text'"
            v-model="localFormData[field.key]"
            :id="`bulk-${field.key}`"
            :placeholder="fieldPlaceholder(field)"
            :disabled="isFieldDisabled(field)"
          />

          <!-- Type: number (.number keeps the payload numeric) -->
          <b-form-input
            v-else-if="field.type === 'number'"
            v-model.number="localFormData[field.key]"
            type="number"
            :id="`bulk-${field.key}`"
            :placeholder="fieldPlaceholder(field)"
            :disabled="isFieldDisabled(field)"
          />
        </b-form-group>
      </b-col>
    </b-row>
  </b-form>
</template>

<script>
import vSelect from "vue-select";
import { computed } from "@vue/composition-api";
import popperHelper from "@/libs/popper-helper";
import i18n from "@/libs/i18n";

export default {
  name: "BulkEditFieldsForm",
  components: {
    vSelect,
  },
  props: {
    fields: {
      type: Array,
      required: true,
    },
    formData: {
      type: Object,
      required: true,
    },
    catalogOptions: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props) {
    // Reactive proxy of formData for two-way binding on the fields
    const localFormData = computed(() => props.formData);

    const getFieldOptions = (field) => {
      return props.catalogOptions[field.optionsKey] || [];
    };

    // Empty multiselects go back to null so the field counts as untouched
    // (an empty array would mean "replace with nothing").
    const setMultiselectValue = (field, value) => {
      localFormData.value[field.key] =
        Array.isArray(value) && value.length ? value : null;
    };

    const isFieldDisabled = (field) =>
      Boolean(field.dependsOn) && !props.formData[field.dependsOn];

    const fieldPlaceholder = (field) => {
      if (!isFieldDisabled(field)) {
        return i18n.t("bulk_edit.select_placeholder");
      }

      const parentField = props.fields.find((f) => f.key === field.dependsOn);

      return i18n.t("bulk_edit.select_dependency_placeholder", {
        field: parentField ? i18n.tc(parentField.label, 1) : "",
      });
    };

    return {
      localFormData,
      getFieldOptions,
      setMultiselectValue,
      isFieldDisabled,
      fieldPlaceholder,
      withPopper: popperHelper.withPopper,
    };
  },
};
</script>
